import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeDollarSign,
  BarChart3,
  Building2,
  Compass,
  Handshake,
  Landmark,
  LineChart,
  MapPinned,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
} from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import SectionHeader from "@/components/sections/SectionHeader";
import Checklist from "@/components/sections/Checklist";
import FeatureGrid from "@/components/sections/FeatureGrid";
import CardGrid from "@/components/sections/CardGrid";
import ComparisonBlock from "@/components/sections/ComparisonBlock";
import TestimonialsSection from "@/components/TestimonialsSection";
import BuyerStrategySection from "@/components/sections/BuyerStrategySection";
import AccordionGroup from "@/components/sections/AccordionGroup";
import SplitSection from "@/components/sections/SplitSection";
import heroImage from "@/assets/invest-hero-mass.jpg";
import ctaInvestImage from "@/assets/invest-cta-mass.jpg";
import processImage from "@/assets/invest-process-mass.jpg";
import strategyImage from "@/assets/invest-strategy-mass.jpg";
import guidedGoalLedImage from "@/assets/invest-guided-goal-led.jpg";
import guidedPerformanceImage from "@/assets/invest-guided-performance.jpg";
import guidedMarketImage from "@/assets/invest-guided-market.jpg";
import guidedDecisionImage from "@/assets/invest-guided-decision.jpg";
import scenarioIncomeImage from "@/assets/invest-scenario-income.jpg";
import scenarioValueAddImage from "@/assets/invest-scenario-valueadd.jpg";

const investorGuidedExperience = [
  {
    icon: Target,
    title: "Goal-led strategy",
    description:
      "We start with your risk tolerance, timeline, and investment objectives before evaluating properties.",
    image: guidedGoalLedImage,
  },
  {
    icon: BarChart3,
    title: "Performance lens",
    description:
      "Every opportunity is viewed through cash flow, appreciation, renovation upside, and exit potential.",
    image: guidedPerformanceImage,
  },
  {
    icon: Compass,
    title: "Market perspective",
    description:
      "We help you understand which market, asset type, and strategy best fit the move you want to make.",
    image: guidedMarketImage,
  },
  {
    icon: Handshake,
    title: "Decision support",
    description:
      "You get guidance that keeps the process strategic instead of reactive or trend-driven.",
    image: guidedDecisionImage,
  },
];

const investmentSteps = [
  {
    label: "Step 01",
    title: "Goals, budget & strategy",
    description:
      "We clarify what you want this investment to do, how you want it to perform, and what parameters matter most.",
  },
  {
    label: "Step 02",
    title: "Market & opportunity screening",
    description:
      "We narrow the search to properties and markets that align with your return goals, risk profile, and timeline.",
  },
  {
    label: "Step 03",
    title: "Deal analysis",
    description:
      "We evaluate income potential, appreciation, improvement opportunities, and overall fit within your strategy.",
  },
  {
    label: "Step 04",
    title: "Offer, due diligence & planning",
    description:
      "We guide negotiations and help you think through next-stage execution, from repairs to rentals to resale.",
  },
  {
    label: "Step 05",
    title: "Close with a longer view",
    description:
      "The purchase is not the finish line. We keep the focus on performance, portfolio fit, and long-term value.",
  },
];

const investmentPaths = [
  {
    icon: Building2,
    title: "Long-term rentals",
    description:
      "Massachusetts rentals positioned for steady tenant demand, cash flow, and long-term appreciation.",
  },
  {
    icon: Landmark,
    title: "Multifamily investing",
    description:
      "Two- to four-unit properties that can support owner-occupant investors or long-term portfolio growth.",
  },
  {
    icon: BadgeDollarSign,
    title: "Fix-and-flip opportunities",
    description:
      "Properties with renovation upside for investors who want to improve value and reposition strategically.",
  },
  {
    icon: Sparkles,
    title: "First investment property",
    description:
      "A practical starting point for first-time investors who want guidance on market fit, financing, and performance.",
  },
  {
    icon: TrendingUp,
    title: "Value-add properties",
    description:
      "Assets where stronger management, updates, or improved positioning can unlock additional income and appreciation.",
  },
];

const investorValues = [
  {
    icon: LineChart,
    title: "Numbers-first thinking",
    description:
      "Advice grounded in return potential, value creation, and long-term portfolio logic.",
  },
  {
    icon: MapPinned,
    title: "Massachusetts market insight",
    description:
      "Guidance grounded in local rental demand, commuter access, neighborhood trends, and market selection.",
  },
  {
    icon: ShieldCheck,
    title: "Risk-aware decisions",
    description:
      "We help you weigh upside against costs, timing, condition, and operational realities.",
  },
  {
    icon: TrendingUp,
    title: "Portfolio perspective",
    description:
      "The focus is not just on one purchase, but on how each move supports broader wealth-building goals.",
  },
  {
    icon: Handshake,
    title: "Strategic partnership",
    description:
      "You get a more thoughtful relationship than a typical transaction-only real estate experience.",
  },
];

const investmentScenarios = [
  {
    title: "First-time investor in a small multifamily",
    description:
      "A first purchase structured around house hacking, stable rental income, and a manageable entry point for a new investor.",
    image: scenarioIncomeImage,
  },
  {
    title: "Buy-and-hold in a commuter market",
    description:
      "A rental property selected for dependable tenant demand, commuter convenience, and long-term appreciation potential.",
    image: scenarioValueAddImage,
  },
  {
    title: "Value-add property with renovation upside",
    description:
      "An acquisition evaluated for renovation scope, after-repair value, and how the improvements could strengthen returns.",
    image: processImage,
  },
];

const comparisonRows = [
  "Best fit",
  "Rental demand",
  "Entry complexity",
  "Upside potential",
  "Investor considerations",
];

const comparisonColumns = [
  {
    title: "Single-family rental",
    values: [
      "Steady buy-and-hold investors",
      "Consistent in strong school and commuter markets",
      "Moderate",
      "Reliable appreciation with stable occupancy",
      "Works well for investors prioritizing simplicity and long-term stability",
    ],
  },
  {
    title: "Multifamily property",
    highlighted: true,
    values: [
      "Investors seeking stronger income potential",
      "Often stronger because of multiple units and flexible tenancy",
      "Higher",
      "Cash flow and house-hack potential",
      "Requires sharper due diligence on expenses, condition, and tenant profile",
    ],
  },
  {
    title: "Condo investment",
    values: [
      "Lower-maintenance entry points",
      "Varies by building rules and neighborhood demand",
      "Moderate",
      "Stable appreciation in the right submarket",
      "Review HOA fees, rental restrictions, and building financials carefully",
    ],
  },
  {
    title: "Fix-and-flip opportunity",
    values: [
      "Experienced or renovation-ready investors",
      "Not the primary driver of returns",
      "High",
      "Strong if scope, budget, and resale positioning are right",
      "Success depends on renovation discipline, timeline control, and accurate after-repair value",
    ],
  },
];

const investorTestimonials = [
  {
    name: "First-Time Investor",
    text: "What stood out most was the strategy. Instead of pushing properties, Hadassah Homes helped me think clearly about performance, financing, and what actually fit my goals.",
  },
  {
    name: "Rental Property Buyer",
    text: "I did not need more listings. I needed perspective. The guidance helped me understand what to look for, what to avoid, and how to compare markets before making my next move.",
  },
  {
    name: "Multifamily Investor",
    text: "The process felt thoughtful from day one. Every conversation stayed focused on rental demand, long-term upside, and whether the deal actually made sense for my goals.",
  },
];

const faqItems = [
  {
    title: "Do I need experience to invest in Massachusetts real estate?",
    content:
      "No. We work with both first-time and experienced investors, helping each client build a strategy that fits their current level, budget, and long-term goals.",
  },
  {
    title: "What type of investment property should I start with?",
    content:
      "That depends on your budget, financing, timeline, and appetite for complexity. We help you compare options like single-family rentals, small multifamilies, condos, and value-add properties.",
  },
  {
    title: "Can you help me evaluate rental income and long-term upside?",
    content:
      "Yes. We look at rental demand, operating considerations, appreciation potential, condition, and the broader strategy behind the deal so you can make a more informed decision.",
  },
  {
    title: "Do you help first-time investors?",
    content:
      "Absolutely. We help first-time investors understand financing, market selection, due diligence, and the tradeoffs between different property types before they move forward.",
  },
  {
    title: "Can you help me compare different Massachusetts markets?",
    content:
      "Yes. We help investors compare markets based on rental demand, price point, commuter access, neighborhood trajectory, and the type of strategy they want to pursue.",
  },
];

const InvestInRealEstate = () => {
  useEffect(() => {
    document.title = "Invest in Massachusetts Real Estate | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Invest in Massachusetts real estate with strategic guidance on rental properties, multifamily opportunities, market selection, and long-term portfolio growth."
      );
    }
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://hadassahhomesinc.com/invest";
  }, []);

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Invest"
        title="Invest in Massachusetts Real Estate with a Clear Strategy"
        subtitle="Build wealth through smarter Massachusetts property decisions with strategic guidance on rentals, multifamily opportunities, and long-term upside."
        backgroundImage={heroImage}
      />

      <BuyerStrategySection
        subheading="Investment Strategy"
        heading="Investing well means thinking beyond the listing."
        description="Massachusetts real estate can be one of the most powerful ways to build long-term wealth, but only when each move has real strategy behind it. Hadassah Homes helps investors evaluate opportunities through cash flow, appreciation, rental demand, value-add potential, and overall portfolio fit so you can make decisions with more clarity and less guesswork."
        image={strategyImage}
        imageAlt="Massachusetts investment property representing strategic real estate investing"
        cta={{ label: "Book a Consultation", href: "/consultation" }}
        secondaryCta={{ label: "Explore Properties", href: "/listings" }}
        features={[
          {
            icon: Target,
            title: "Defined investment goals",
            description: "Start with a strategy built around what you want the property to do.",
          },
          {
            icon: LineChart,
            title: "Performance analysis",
            description: "Evaluate income, appreciation, and upside with stronger context.",
          },
          {
            icon: Compass,
            title: "Market fit",
            description: "Compare opportunities based on the market and path that suit you best.",
          },
          {
            icon: ShieldCheck,
            title: "Risk-aware decisions",
            description: "Move forward with a clearer view of tradeoffs, timing, and long-term value.",
          },
        ]}
      />

      <ContentSection variant="dark" className="py-20 md:py-28">
        <div className="mx-auto max-w-wide grid grid-cols-1 lg:grid-cols-[0.42fr_0.58fr] gap-10 md:gap-14 lg:gap-20 xl:gap-24 items-start">
          <div className="space-y-7 md:space-y-8 lg:pt-1">
            <span className="font-body text-xs uppercase tracking-[0.14em] text-accent">
              Guided Experience
            </span>
            <h2 className="max-w-2xl font-headline text-4xl md:text-5xl lg:text-6xl text-secondary-foreground leading-[1.04]">
              What It&apos;s Like to Invest With Us
            </h2>
            <p className="max-w-xl text-secondary-foreground/80 text-base md:text-lg leading-[1.85]">
              This is a more strategic investment experience. We help you move with clearer
              goals, stronger analysis, and better market perspective so every opportunity is
              evaluated with intention.
            </p>
            <div className="border-l-2 border-accent/60 pl-5 py-2 md:py-3 mt-4 md:mt-6">
              <p className="font-headline text-2xl text-secondary-foreground leading-snug">
                Thoughtful, numbers-aware guidance from the first conversation to the final decision.
              </p>
            </div>
          </div>

          <div>
            <article className="group relative min-h-[340px] md:min-h-[400px] lg:min-h-[430px] overflow-hidden">
              <img
                src={investorGuidedExperience[0].image}
                alt={investorGuidedExperience[0].title}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/75 to-black/35" />

              <div className="relative z-10 flex h-full items-end p-6 md:p-8 lg:p-9">
                <div className="max-w-[88%] space-y-4 bg-black/45 px-5 py-4 md:max-w-[78%] lg:max-w-[74%]">
                  <p className="font-body text-[10px] uppercase tracking-[0.2em] text-white/75">
                    Guided Experience 01
                  </p>
                  <h3 className="font-headline text-[2.05rem] md:text-[2.45rem] leading-[1.08] text-white">
                    {investorGuidedExperience[0].title}
                  </h3>
                  <p className="text-white/90 text-base md:text-lg leading-[1.8]">
                    {investorGuidedExperience[0].description}
                  </p>
                </div>
              </div>
            </article>

            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:gap-9">
              {investorGuidedExperience.slice(1).map((item, idx) => (
                <article
                  key={item.title}
                  className={[
                    "group relative min-h-[250px] md:min-h-[290px] lg:min-h-[310px] overflow-hidden",
                    idx < 2 ? "xl:min-h-[330px]" : "xl:min-h-[300px]",
                    idx === 2 ? "lg:col-span-2" : "",
                  ].join(" ")}
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/75 to-black/35" />

                  <div className="relative z-10 flex h-full items-end p-5 md:p-7 lg:p-8">
                    <div className="max-w-full space-y-3 bg-black/45 px-4 py-3 md:max-w-[92%] md:px-5 md:py-4 lg:max-w-[94%]">
                      <p className="font-body text-[10px] uppercase tracking-[0.2em] text-white/75">
                        Guided Experience {String(idx + 2).padStart(2, "0")}
                      </p>
                      <h3 className="font-headline text-[1.55rem] md:text-[1.75rem] leading-[1.12] text-white">
                        {item.title}
                      </h3>
                      <p className="text-white/90 text-sm md:text-base leading-[1.7]">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </ContentSection>

      <ContentSection>
        <div className="grid grid-cols-1 lg:grid-cols-[0.92fr_1.08fr] gap-12 md:gap-16 items-start">
          <div className="lg:self-start">
            <div className="relative lg:sticky lg:top-32">
              <div className="relative">
                <div className="overflow-hidden lg:h-[min(74vh,780px)]">
                  <img
                    src={processImage}
                    alt="Massachusetts real estate investing opportunity"
                    className="w-full h-full aspect-[4/5] object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="absolute -bottom-6 right-6 md:right-8 bg-card border border-border shadow-xl p-5 max-w-[240px]">
                  <p className="font-body text-xs uppercase tracking-[0.14em] text-accent mb-2">
                    Investor Process
                  </p>
                  <p className="text-muted-foreground text-base leading-relaxed">
                    A structured path that keeps each move tied to performance and long-term value.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <SectionHeader
              eyebrow="Process"
              title="The Investment Process, Simplified"
              description="We break the journey into clear stages so you can evaluate opportunities with more confidence and less noise."
              align="left"
            />
            <div className="space-y-8 md:space-y-10">
              {investmentSteps.map((step, index) => (
                <div
                  key={step.title}
                  className="relative bg-card border border-border shadow-sm p-6 md:p-8 lg:min-h-[210px]"
                >
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-accent/85" aria-hidden="true" />
                  <div className="pl-3">
                    <p className="font-body text-xs uppercase tracking-[0.14em] text-accent mb-3">
                      {step.label}
                    </p>
                    <h3 className="font-headline text-2xl md:text-3xl text-foreground mb-3 leading-tight">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground text-base md:text-lg leading-[1.8] max-w-2xl">
                      {step.description}
                    </p>
                  </div>
                  {index < investmentSteps.length - 1 && (
                    <div
                      className="hidden lg:block absolute left-[14px] -bottom-8 h-8 w-px bg-border"
                      aria-hidden="true"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SectionHeader
          eyebrow="Investment Paths"
          title="Strategies We Help You Navigate"
          description="The right path depends on your goals, timeline, budget, and how you want the asset to perform."
        />
        <FeatureGrid items={investmentPaths} columns={2} />
      </ContentSection>

      <ContentSection>
        <div className="mx-auto max-w-3xl space-y-8 lg:max-w-4xl">
          <div className="space-y-5">
            <span className="font-body text-xs uppercase tracking-[0.14em] text-accent">
              Market Comparison
            </span>
            <h2 className="font-headline text-3xl md:text-5xl text-foreground leading-tight">
              Compare Massachusetts Investment Paths More Strategically
            </h2>
            <p className="text-muted-foreground text-base md:text-lg leading-[1.75]">
              We help you compare property types based on strategy fit, demand, complexity, and upside so you can choose a path that actually supports your goals.
            </p>
          </div>

          <div className="hidden md:block lg:-mx-8 xl:-mx-14 2xl:-mx-20">
            <ComparisonBlock
              rows={comparisonRows}
              columns={comparisonColumns}
              className="overflow-hidden border-border/80 bg-card/95"
              tableClassName="md:min-w-[980px] lg:min-w-0"
              headerCellClassName="md:px-5 md:py-6 lg:px-6 lg:py-7 xl:px-7 xl:py-8 align-top leading-snug"
              rowLabelClassName="md:w-[23%] md:px-5 md:py-5 lg:px-6 lg:py-6 xl:px-7 xl:py-7 leading-relaxed"
              cellClassName="md:px-5 md:py-5 lg:px-6 lg:py-6 xl:px-7 xl:py-7 align-top leading-relaxed"
            />
          </div>

          <div className="space-y-4 md:hidden">
            {comparisonColumns.map((column) => (
              <article
                key={column.title}
                className={[
                  "border bg-card px-5 py-6 shadow-sm",
                  column.highlighted ? "border-accent/45" : "border-border",
                ].join(" ")}
              >
                <div className="space-y-2">
                  <h3 className="font-headline text-2xl leading-tight text-foreground">
                    {column.title}
                  </h3>
                  {column.highlighted && (
                    <p className="font-body text-[11px] uppercase tracking-[0.16em] text-accent">
                      Recommended
                    </p>
                  )}
                </div>

                <div className="mt-5 space-y-4">
                  {comparisonRows.map((row, index) => (
                    <div key={`${column.title}-${row}`} className="border-t border-border/70 pt-4 first:border-t-0 first:pt-0">
                      <p className="font-body text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                        {row}
                      </p>
                      <p className="mt-2 text-sm leading-relaxed text-foreground">
                        {String(column.values[index])}
                      </p>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SplitSection
          title="New to Investing? Start With a Better Foundation"
          description="You do not need a large portfolio to begin, but you do need a strategy that helps you evaluate opportunities with more confidence."
          image={guidedGoalLedImage}
          imageAlt="Massachusetts investor reviewing real estate opportunities"
        >
          <Checklist
            columns={2}
            items={[
              "Understand how different investment paths work",
              "Identify realistic entry points based on your budget",
              "Avoid common and expensive early mistakes",
              "Build confidence before making your first move",
            ]}
          />
          <p className="text-secondary-foreground/82 text-base md:text-lg leading-relaxed pt-3">
            The first deal matters. Getting the strategy right early helps everything else
            compound more effectively.
          </p>
        </SplitSection>
      </ContentSection>

      <ContentSection>
        <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-12 md:gap-16 items-start">
          <div className="space-y-7">
            <SectionHeader
              eyebrow="Investor Value"
              title="Why Investors Choose Hadassah Homes"
              description="The experience is designed to keep decisions more strategic, more informed, and better aligned with long-term wealth goals."
              align="left"
            />
            <p className="text-muted-foreground text-base md:text-lg leading-[1.75]">
              We do not approach investment opportunities like standard home searches. The
              focus stays on performance, fit, and the bigger picture behind every acquisition.
            </p>
            <p className="border-l-2 border-accent pl-5 text-secondary text-base md:text-lg leading-relaxed max-w-xl">
              The focus stays on performance, fit, and the bigger picture behind every
              acquisition.
            </p>
          </div>

          <div className="space-y-1">
            {investorValues.map((value, index) => (
              <article
                key={value.title}
                className="grid grid-cols-[auto_1fr] gap-x-5 md:gap-x-6 gap-y-2 md:gap-y-3 py-6 md:py-7 border-b border-border/75 last:border-b-0"
              >
                <span className="pt-0.5 text-xs font-body tracking-[0.18em] uppercase text-accent">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div className="space-y-2 md:space-y-3">
                  <h3 className="font-headline text-2xl md:text-[1.75rem] leading-tight text-secondary">
                    {value.title}
                  </h3>
                  <p className="text-muted-foreground text-base md:text-lg leading-[1.75]">
                    {value.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SectionHeader
          eyebrow="Scenarios"
          title="Example Investment Scenarios"
          description="Illustrative examples of how thoughtful analysis can shape stronger real estate decisions."
        />
        <CardGrid items={investmentScenarios} columns={3} />
      </ContentSection>

      <TestimonialsSection
        sectionId="investor-testimonials"
        title="Investor Perspectives"
        testimonials={investorTestimonials}
        showGoogleSummary={false}
        reviewSourceLabel="Investor feedback"
      />

      <ContentSection variant="dark">
        <SectionHeader
          title="Frequently Asked Questions"
          description="Straightforward answers for investors exploring their next move."
        />
        <div className="max-w-3xl mx-auto">
          <AccordionGroup items={faqItems} />
        </div>
      </ContentSection>

      <section className="relative overflow-hidden py-24 md:py-32">
        <img
          src={ctaInvestImage}
          alt="Investment-focused real estate call to action"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/85 via-black/60 to-black/30" />
        <div className="relative z-20 mx-auto max-w-wide px-5 md:px-12">
          <div className="mx-auto max-w-3xl text-center space-y-6 md:space-y-7">
            <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl leading-tight text-white">
              Let&apos;s Build Your Investment Strategy
            </h2>
            <p className="text-white/90 text-base md:text-lg leading-relaxed">
              The best investments start with clarity, not guesswork. Whether you are
              exploring your first deal or looking to grow your portfolio, we can help you
              move forward with a stronger plan.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
              <Link
                to="/consultation"
                className="inline-flex items-center justify-center font-body text-sm uppercase tracking-[0.12em] px-10 py-4 bg-accent text-accent-foreground hover:bg-accent/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-black/70"
              >
                Book a Consultation
              </Link>
              <Link
                to="/buy-a-home"
                className="inline-flex items-center justify-center font-body text-sm uppercase tracking-[0.12em] px-10 py-4 border border-white/40 text-white hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-black/70"
              >
                Explore Buying
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default InvestInRealEstate;
